import {
  AlarmIcon,
  AssetIcon,
  BankIcon,
  BuildingIcon,
  DateIcon,
  DocumentCopyIcon,
  DocumentIcon,
  DollarIcon,
  EditPenIcon,
  EmailIcon,
  FileSettingsIcon,
  PeopleIcon,
  RunIcon,
  TaskIcon,
  WalletIcon,
} from '@sharedCR/coreDesignSystem/icons';
import { BorrowerTabIcon, FeesTabIcon } from 'assets/icons';
import _ from 'lodash';

// Loan editor split layout: editor pane + up to this many document side-panes.
export const MAX_DOCUMENT_PANES = 2;
// Minimum usable pixel widths used both to gate "add a pane" and as react-split minSize.
// A document pane hosts the FULL Documents browser (tree + viewer + inspector); the module lays
// out for its width, so below ~900px the PDF viewer is squeezed out. Keep the floor at the usable
// threshold so a 2nd pane is only offered (and only sized) when the window genuinely has room —
// one full browser fits a normal ultrawide; two need a wide multi-monitor / very-wide display.
export const EDITOR_PANE_MIN_WIDTH = 640;
export const DOCUMENT_PANE_MIN_WIDTH = 900;

export const MAX_COBORROWER_NUMBER = 3;
export const MAX_NONBORROWER_NUMBER = 4;
export const MAX_PERSON_YEARS = 99;
export const MIN_ALTERNATE_NAMES_NUMBER = 1;
export const MIN_OTHER_EXPENSES_NUMBER = 1;
export const MAX_OTHER_EXPENSES_NUMBER = 10;
export const MIN_YEAR_OF_BUILD = '1600';
export const MAX_REMAINING_MONTHS_NUMBER = 9999;
export const ADDRESS_PREFIX = 'address';
export const DEFAULT_COUNTRY = 'USA';
export const LOAN_APPLICATION_ACTION_NAME_PREFIX = 'loanApplication*';

// Intent of a pending borrower -> CRM contact change held in the borrower edit.
export const PENDING_CONTACT_LINK_INTENT = {
  LINK: 'link',
  UNLINK: 'unlink',
};
export const CASH_OUT_FANNIE_FACTOR = 0.02;
export const CASH_OUT_FANNIE_MIN_AMOUNT = 2000;
export const LOAN_APPLICATION_TAB_ENTITY_UI_MANAGE_ID_SEPARATOR = '__';
export const LICENSE_NUMBER_MAX_LENGTH = 30;
export const MAX_BORROWERS_COUNT = 4;
export const MAX_NBE_COUNT = 4;
export const CALCULATED_AFTER_MODAL_SAVED_PLACEHOLDER =
  'Will be calculated after the modal window is saved';

// a lot of legacy tabs because of compability with loan history and BE metadata information
export const LoanApplicationTabName = {
  ACTIVITIES: 'Activities',
  UNIFORM_RESIDENTIAL: '1003',
  LOAN: 'Loan',
  TASKS: 'Tasks',
  UNDERWRITING: 'Underwriting',
  UNDERWRITING_NEW: 'Underwriting NEW',
  DOCUMENTS: 'Documents',
  CLOSING: 'Closing',
  CLOSING_NEW: 'Closing NEW',
  POST_CLOSING: 'Post Closing',
  CHECKLISTS: 'Checklists',
  KEY_DATES: 'Key Dates',
  CONDITIONS: 'Conditions',
  UW_REPORTS: 'UW Reports',
  LOCK_INFO: 'Lock Info',
  LEAD: 'Lead',
  PAYMENTS: 'Payments',
  PERSONAL_INFORMATION: 'Personal Info/Credit',
  ADDRESS: 'Address',
  PROPERTY: 'Property',
  REO: 'REO',
  INCOME_AND_EMPLOYMENT: 'Income & Employment',
  PROPERTY_AND_LOAN: 'Property and Loan',
  CONTACTS: 'Contacts',
  ASSETS: 'Assets',
  LIABILITIES: 'Liabilities',
  TRANSACTION: 'Transaction',
  DECLARATIONS: 'Declarations',
  LOAN_ORIGINATOR: 'Loan Originator',
  LOAN_ROLES: 'Loan Roles',
  LOAN_FEES: 'Fees',
  LOAN_TAGS: 'Tags',
  BORROWERS: 'Borrowers',
  FINANCIAL_INFO: 'Financial Information',
  ALERTS: 'Alerts',
  LOANS: 'Loans',
  LOAN_SUMMARY: 'Loan Summary',
  SERVICING: 'Servicing',
  LOAN_EXCEPTIONS: 'Exceptions',
  AI_ACTIVITY: 'AI Activity',
  CONSTRUCTION: 'Construction',
  VENDOR_REQUESTS: 'Vendor Requests',
};

const LeadSubtab = {
  LEAD_INFO: {
    tabName: 'Lead Info',
    tabRoute: 'lead-info',
  },
  LOAN_ROLES: {
    tabName: 'Loan roles',
    tabRoute: 'loan-roles',
    getIsAvailable: ({ isRolesAllowed }) => isRolesAllowed,
  },
  LOAN_TAGS: {
    tabName: 'Tags',
    tabRoute: 'tags',
    getIsAvailable: ({ isTagsAvailable }) => isTagsAvailable,
  },
};

export const SpouseRelationTypeEnum = {
  Borrower1: 'Borrower1',
  Borrower2: 'Borrower2',
  Borrower3: 'Borrower3',
  Borrower4: 'Borrower4',
  NonBorrowerEntity1: 'NonBorrowerEntity1',
  NonBorrowerEntity2: 'NonBorrowerEntity2',
  NonBorrowerEntity3: 'NonBorrowerEntity3',
  NonBorrowerEntity4: 'NonBorrowerEntity4',
  Other: 'Other',
};

const LoanSubtab = {
  CHECKLISTS: {
    tabName: 'Checklists',
    tabRoute: 'checklists',
    getIsAvailable: ({ isChecklistsAllowed }) => isChecklistsAllowed,
  },
};

export const ActivitiesSubtab = {
  ALL: {
    tabName: 'All',
    tabRoute: 'all',
  },
  EMAILS: {
    tabName: 'Emails',
    tabRoute: 'emails',
  },
  CALLS: {
    tabName: 'Calls',
    tabRoute: 'calls',
    getIsAvailable: ({ isCallsAllowed }) => isCallsAllowed,
  },
  INTERNAL_CHAT: {
    tabName: 'Internal Chat',
    tabRoute: 'internal-chat',
    getIsAvailable: ({ isInternalChatAllowed }) => isInternalChatAllowed,
  },
};

export const TasksSubtab = {
  PENDING: { tabName: 'Pending', tabRoute: 'pending' },
  TO_REVIEW: { tabName: 'Ops to review', tabRoute: 'ops-to-review' },
  COMPLETED: { tabName: 'Completed', tabRoute: 'completed' },
  REJECTED: { tabName: 'Rejected', tabRoute: 'rejected' },
  CANCELED: { tabName: 'Canceled', tabRoute: 'canceled' },
};

export const DocumentsSubtabEnum = {
  CIC_HISTORY: 'cic-history',
  DISCLOSURES: 'disclosures',
  DOCUMENTS: 'documents',
  FORM_GENERATION: 'form-generation',
  LOAN_SPECIFIC: 'loan-specific',
};

// Listed alphabetically: the sidebar renders the Documents sub-tabs in this key order.
const DocumentsSubtab = {
  CIC_HISTORY: {
    tabName: 'CIC History',
    tabRoute: DocumentsSubtabEnum.CIC_HISTORY,
    getIsAvailable: ({ isCicHistoryViewAllowed }) => !!isCicHistoryViewAllowed,
  },
  DISCLOSURES: {
    tabName: 'Disclosures',
    tabRoute: DocumentsSubtabEnum.DISCLOSURES,
    getIsAvailable: ({ isFormDeliveryViewAllowed }) => !!isFormDeliveryViewAllowed,
  },
  DOCUMENTS: {
    tabName: 'Documents',
    tabRoute: DocumentsSubtabEnum.DOCUMENTS,
  },
  FORM_GENERATION: {
    tabName: 'Form Generation',
    tabRoute: DocumentsSubtabEnum.FORM_GENERATION,
  },
  LOAN_SPECIFIC: {
    tabName: 'Loan Specific',
    tabRoute: DocumentsSubtabEnum.LOAN_SPECIFIC,
    getIsAvailable: ({ isHideLoanSpecificForm }) => !isHideLoanSpecificForm,
  },
};

export const UnderwritingSubtab = {
  UNDERWRITING: {
    tabName: 'Underwriting',
    tabRoute: 'Underwriting',
    getIsAvailable: ({ isUnderwritingAllowed }) => isUnderwritingAllowed,
  },
  CONDITIONS: {
    tabName: 'Conditions',
    tabRoute: 'conditions',
    getIsAvailable: ({ isConditionsAllowed }) => isConditionsAllowed,
  },
  KEY_DATES: {
    tabName: 'Key Dates',
    tabRoute: 'key-dates',
    getIsAvailable: ({ isKeyDatesAllowed, isUnsecuredLoan }) =>
      isKeyDatesAllowed && !isUnsecuredLoan,
  },
  UW_REPORTS: {
    tabName: 'UW Reports',
    tabRoute: 'uw-reports',
    getIsAvailable: ({ isReportsAllowed }) => isReportsAllowed,
  },
};

const LoansSubtab = {
  LOAN_SUMMARY: {
    tabName: 'Loan Summary',
    Icon: DocumentIcon,
  },
  ACTIVITIES: {
    tabName: 'Activities',
    getIsAvailable: ({ isActivitiesTabAllowed }) => isActivitiesTabAllowed,
    Icon: EmailIcon,
  },
  AI_ACTIVITY: {
    tabName: 'AI Activity',
    getIsAvailable: ({ isAiActivityAllowed }) => isAiActivityAllowed,
    Icon: RunIcon,
  },
  PROPERTY_AND_LOAN: {
    tabName: 'Property and Loan',
    getCustomName: ({ isUnsecuredLoan }) => (isUnsecuredLoan ? 'Loan' : 'Property & Loan'),
    Icon: BankIcon,
  },
  CONTACTS: {
    tabName: 'Contacts',
    Icon: PeopleIcon,
    getIsAvailable: ({ isContactsAllowed }) => isContactsAllowed,
  },
  BORROWERS: {
    tabName: 'Borrowers',
    customName: 'Borrower(s)',
    Icon: BorrowerTabIcon,
  },
  FINANCIAL_INFO: {
    tabName: 'Financial Information',
    Icon: AssetIcon,
  },
  TASKS: {
    tabName: 'Tasks',
    Icon: TaskIcon,
    getIsAvailable: ({ isTasksAllowed }) => isTasksAllowed,
  },
  UNDERWRITING: {
    tabName: 'Underwriting',
    customName: 'Underwriting Old',
    Icon: EditPenIcon,
    getIsAvailable: ({
      isUnderwritingAllowed,
      isLegacyUnderwritingTabDisabled,
      isRestyledUnderwritingTabEnabled,
    }) =>
      isUnderwritingAllowed &&
      !isLegacyUnderwritingTabDisabled &&
      !isRestyledUnderwritingTabEnabled,
  },
  UNDERWRITING_NEW: {
    tabName: 'Underwriting NEW',
    customName: 'Underwriting',
    Icon: FileSettingsIcon,
    getIsAvailable: ({ isRestyledUnderwritingTabEnabled, isUnderwritingAllowed }) =>
      isRestyledUnderwritingTabEnabled && isUnderwritingAllowed,
  },
  CLOSING_NEW: {
    tabName: 'Closing NEW',
    customName: 'Closing',
    Icon: DateIcon,
    getIsAvailable: ({ isClosingAllowed }) => isClosingAllowed,
  },
  CONSTRUCTION: {
    tabName: 'Construction',
    Icon: BuildingIcon,
    getIsAvailable: ({ isConstructionAllowed, isConstructionLoan, isConstructionEditAllowed }) =>
      isConstructionAllowed && (isConstructionLoan || isConstructionEditAllowed),
  },
  POST_CLOSING: {
    tabName: 'Post Closing',
    Icon: DateIcon,
    getIsAvailable: ({ isPostClosingAllowed }) => isPostClosingAllowed,
  },
  LOAN_FEES: {
    tabName: 'Fees',
    Icon: FeesTabIcon,
    getIsAvailable: ({ isLoanFeesAllowed }) => isLoanFeesAllowed,
  },
  SERVICING: {
    tabName: 'Servicing',
    Icon: DollarIcon,
    getIsAvailable: ({ isServicingAllowed }) => isServicingAllowed,
  },
  LOAN_EXCEPTIONS: {
    tabName: 'Exceptions',
    Icon: AlarmIcon,
    getIsAvailable: ({ isLoanExceptionsAllowed }) => isLoanExceptionsAllowed,
  },
  VENDOR_REQUESTS: {
    tabName: 'Vendor Requests',
    Icon: RunIcon,
    getIsAvailable: ({ isVendorRequestsAllowed }) => isVendorRequestsAllowed,
  },
};

export const LoanNavigationConfig = {
  LOANS: {
    tabName: 'Loans',
    customName: 'Loan',
    subtabs: LoansSubtab,
    Icon: WalletIcon,
    isExpanded: true,
  },
  DOCUMENTS: {
    tabName: 'Documents',
    subtabs: DocumentsSubtab,
    Icon: DocumentCopyIcon,
    getIsAvailable: ({ isDocumentsAllowed }) => isDocumentsAllowed,
  },
  ALERTS: {
    tabName: 'Alerts',
    Icon: AlarmIcon,
    injectSubtabs: ({ checklistResultsSubtabs }) => checklistResultsSubtabs,
    isAlwaysExpanded: true,
    hasLink: true,
    getIsAvailable: ({ isChecklistsAllowed }) => isChecklistsAllowed,
  },
};

export const TasksSubtabRouteParam = _.mapValues(TasksSubtab, ({ tabRoute }) => tabRoute);

export const DocumentsSubtabRouteParam = _.mapValues(DocumentsSubtab, ({ tabRoute }) => tabRoute);

export const LoanSubtabRouteParam = _.mapValues(LoanSubtab, ({ tabRoute }) => tabRoute);

export const UnderwritingSubtabRouteParam = _.mapValues(
  UnderwritingSubtab,
  ({ tabRoute }) => tabRoute,
);

export const LeadSubtabRouteParam = _.mapValues(LeadSubtab, ({ tabRoute }) => tabRoute);

export const ActivitiesSubtabRouteParam = _.mapValues(ActivitiesSubtab, ({ tabRoute }) => tabRoute);

export const LoanApplicationTabRouteParam = _.mapValues(LoanApplicationTabName, _.kebabCase);
export const LoanApplicationTabRouteParamInverted = _.invert(LoanApplicationTabRouteParam);

// Retired tab routes and the tab each one moved to, so bookmarks and old links keep working.
// LEN2-798: Parties merged with the Property & Loan Contacts card into the Contacts tab.
export const LEGACY_LOAN_TAB_ROUTE_REDIRECTS = {
  parties: LoanApplicationTabRouteParam.CONTACTS,
};

export const LoanProgramAmortizationTerm = {
  TERM_15: 15,
  TERM_30: 30,
  TERM_45: 45,
  TERM_60: 60,
  TERM_90: 90,
  TERM_120: 120,
  TERM_180: 180,
  CUSTOM: null,
};

export const IncomeType = {
  W2: 'W2',
  SELF_EMPLOYED: 'SelfEmployed',
  ALIMONY: 'Alimony',
  AUTOMOBILE_ALLOWANCE: 'AutomobileAllowance',
  BOARDER_INCOME: 'BoarderIncome',
  CAPITAL_GAINS: 'CapitalGains',
  CHILD_SUPPORT: 'ChildSupport',
  DISABILITY: 'Disability',
  FOSTER_CARE: 'FosterCare',
  HOUSING_OR_PARSONAGE: 'HousingOrParsonage',
  DIVIDENDS_INTEREST: 'DividendsInterest',
  MORTGAGE_CREDIT_CERTIFICATE: 'MortgageCreditCertificate',
  MORTGAGE_DIFFERENTIAL_PAYMENTS: 'MortgageDifferentialPayments',
  NOTES_RECEIVABLE: 'NotesReceivable',
  PUBLIC_ASSISTANCE: 'PublicAssistance',
  RETIREMENT: 'Retirement',
  ROYALTY_PAYMENTS: 'RoyaltyPayments',
  SEPARATE_MAINTENANCE: 'SeparateMaintenance',
  SOCIAL_SECURITY: 'SocialSecurity',
  TRUST: 'Trust',
  UNEMPLOYMENT_BENEFITS: 'UnemploymentBenefits',
  VA_COMPENSATION: 'VACompensation',
  OTHER: 'Other',
  ACCESSORY_UNIT_INCOME: 'AccessoryUnitIncome',
  BORROWER_ESTIMATED_TOTAL_MONTHLY_INCOME: 'BorrowerEstimatedTotalMonthlyIncome',
  DEFINED_CONTRIBUTION_PLAN: 'DefinedContributionPlan',
  EMPLOYMENT_RELATED_ACCOUNT: 'EmploymentRelatedAccount',
  HOUSING_ALLOWANCE: 'HousingAllowance',
  HOUSING_CHOICE_VOUCHER_PROGRAM: 'HousingChoiceVoucherProgram',
  MILITARY_BASE_PAY: 'MilitaryBasePay',
  MILITARY_CLOTHES_ALLOWANCE: 'MilitaryClothesAllowance',
  MILITARY_COMBAT_PAY: 'MilitaryCombatPay',
  MILITARY_FLIGHT_PAY: 'MilitaryFlightPay',
  MILITARY_HAZARD_PAY: 'MilitaryHazardPay',
  MILITARY_OVERSEAS_PAY: 'MilitaryOverseasPay',
  MILITARY_PROP_PAY: 'MilitaryPropPay',
  MILITARY_QUARTERS_ALLOWANCE: 'MilitaryQuartersAllowance',
  MILITARY_RATIONS_ALLOWANCE: 'MilitaryRationsAllowance',
  MILITARY_VARIABLE_HOUSING_ALLOWANCE: 'MilitaryVariableHousingAllowance',
  MISCELLANEOUS_INCOME: 'MiscellaneousIncome',
  NON_BORROWER_CONTRIBUTION: 'NonBorrowerContribution',
  PENSION: 'Pension',
  TEMPORARY_LEAVE: 'TemporaryLeave',
  TIP_INCOME: 'TipIncome',
  TRAILING_CO_BORROWER_INCOME: 'TrailingCoBorrowerIncome',
  VA_BENEFITS_NON_EDUCATIONAL: 'VABenefitsNonEducational',
};

export const EmploymentVerificationType = {
  STANDARD: 'Standard',
  FUNDING: 'Funding',
};

export const EmployerPhoneSource = {
  EMPLOYER_OFFICIAL_WEBSITE: 'EmployerOfficialWebsite',
  ONLINE_BUSINESS_DIRECTORY: 'OnlineBusinessDirectory',
  THIRD_PARTY_VERIFICATION_VENDOR: 'ThirdPartyVerificationVendor',
  PUBLIC_BUSINESS_REGISTRY: 'PublicBusinessRegistry',
  EMPLOYER_PROVIDED_DOCUMENTATION: 'EmployerProvidedDocumentation',
  PREVIOUS_VOE_OR_COMPANY_RECORD_ON_FILE: 'PreviousVoeOrCompanyRecordOnFile',
  BORROWER_PROVIDED_INFORMATION: 'BorrowerProvidedInformation',
  LINKED_IN_COMPANY_PROFILE: 'LinkedInCompanyProfile',
  OTHER: 'Other',
};

export const VerificationMethod = {
  VERBAL: 'Verbal',
  EMAIL: 'Email',
  VENDOR: 'Vendor',
  OTHER: 'Other',
};

export const EmployedAtTimeOfVerification = {
  YES: 'Yes',
  NO: 'No',
  VERIFY_LATER: 'VerifyLater',
};

export const ProbabilityOfContinuedEmployment = {
  GOOD: 'Good',
  FAIR: 'Fair',
  REFUSES_TO_PROVIDE: 'RefusesToProvide',
};

export const BusinessVerificationMethod = {
  DIRECTORY: 'Directory',
  CPA: 'CPA',
  LICENSE: 'License',
  OTHER: 'Other',
};

export const EmploymentOwnershipType = {
  SHARE_LESS_THAN_25_PERCENTS: 'LessThan25Percent',
  SHARE_GREATER_THAN_OR_EQUAL_TO_25_PERCENT: 'GreaterThanOrEqualTo25Percent',
};

export const TaxFilingType = {
  FORM_1040_SCHEDULE_C: 'Form1040ScheduleC',
  FORM_1065: 'Form1065',
  FORM_1120: 'Form1120',
  FORM_1120S: 'Form1120S',
};

export const TitlePerTaxReturnType = {
  PARTNER_OR_LIMITED_PARTNER: 'PartnerOrLimitedPartner',
  PRESIDENT: 'President',
  VICE_PRESIDENT: 'VicePresident',
  SECRETARY: 'Secretary',
  TREASURER: 'Treasurer',
  ASSISTANT_TREASURER: 'AssistantTreasurer',
  CHIEF_ACCOUNTANT_AND_MANAGER: 'ChiefAccountantAndOfficer',
  MANAGING_MEMBER: 'ManagingMember',
};

export const extendedIncomeTypes = [IncomeType.SELF_EMPLOYED, IncomeType.W2];

export const LiabilitiesType = {
  LIABILITY: 'Liability',
  OTHER: 'Other',
};

export const AssetsType = {
  CHECKING: 'Checking',
  SAVINGS: 'Savings',
  MONEY_MARKET: 'MoneyMarket',
  CERTIFICATE_OF_DEPOSIT: 'CertificateOfDeposit',
  MUTUAL_FUND: 'MutualFund',
  STOCKS: 'Stocks',
  STOCK_OPTIONS: 'StockOptions',
  BONDS: 'Bonds',
  STOCKS_BONDS_MUTUAL_FUNDS: 'StocksBondsMutualFunds',
  RETIREMENT: 'Retirement',
  RETIREMENT_ACCOUNT: 'RetirementAccount',
  BRIDGE_LOAN_PROCEEDS: 'BridgeLoanProceeds',
  INDIVIDUAL_DEVELOPMENT_ACCOUNT: 'IndividualDevelopmentAccount',
  TRUST_ACCOUNT: 'TrustAccount',
  CASH_VALUE_OF_LIFE_INSURANCE: 'CashValueOfLifeInsurance',
  GIFT: 'Gift',
  GIFT_OF_PROPERTY_EQUITY: 'GiftOfPropertyEquity',
  PROCEEDS_FROM_REAL_ESTATE: 'ProceedsFromRealEstate',
  PROCEEDS_FROM_SALE: 'ProceedsFromSaleOfNonRealEstateAsset',
  SECURED_BORROWED_FUNDS: 'SecuredBorrowedFunds',
  UNSECURED_BORROWED_FUNDS: 'UnsecuredBorrowedFunds',
  OTHER: 'Other',
  OTHER_NON_LUQUID: 'OtherNonLiquid',
  EARNEST_MONEY: 'EarnestMoney',
  EMPLOYER_ASSISTANCE: 'EmployerAssistance',
  LOT_EQUITY: 'LotEquity',
  RELOCATION_FUNDS: 'RelocationFunds',
  RENT_CREDIT: 'RentCredit',
  SWEAT_EQUITY: 'SweatEquity',
  TRADE_EQUITY: 'TradeEquity',
  GRANT: 'Grant',
};

export const SourceTypeTypes = {
  BORROWER: 'Borrower',
  BUILDER: 'Builder',
  COMMUNITY_NON_PROFIT: 'CommunityNonProfit',
  EMPLOYER: 'Employer',
  FEDERAL_AGENCY: 'FederalAgency',
  INSTITUTIONAL: 'Institutional',
  LENDER: 'Lender',
  LOCAL_AGENCY: 'LocalAgency',
  NON_ORIGINATING_FINANCIAL_INSTITUTION: 'NonOriginatingFinancialInstitution',
  NON_PARENT_RELATIVE: 'NonParentRelative',
  OTHER: 'Other',
  PARENT: 'Parent',
  PROPERTY_SELLER: 'PropertySeller',
  RELATIVE: 'Relative',
  RELIGIOUS_NON_PROFIT: 'ReligiousNonProfit',
  RURAL_HOUSING_SERVICE: 'RuralHousingService',
  STATE_AGENCY: 'StateAgency',
  UNKNOWN: 'Unknown',
  UNMARRIED_PARTNER: 'UnmarriedPartner',
  UNRELATED_FRIEND: 'UnrelatedFriend',
};

export const mainAssetsAccountTypes = [
  AssetsType.CHECKING,
  AssetsType.SAVINGS,
  AssetsType.MONEY_MARKET,
  AssetsType.CERTIFICATE_OF_DEPOSIT,
  AssetsType.MUTUAL_FUND,
  AssetsType.STOCKS,
  AssetsType.STOCK_OPTIONS,
  AssetsType.BONDS,
  AssetsType.RETIREMENT,
  AssetsType.BRIDGE_LOAN_PROCEEDS,
  AssetsType.INDIVIDUAL_DEVELOPMENT_ACCOUNT,
  AssetsType.TRUST_ACCOUNT,
  AssetsType.CASH_VALUE_OF_LIFE_INSURANCE,
];

export const otherAssetsAndCreditsTypes = [
  AssetsType.PROCEEDS_FROM_REAL_ESTATE,
  AssetsType.PROCEEDS_FROM_SALE,
  AssetsType.SECURED_BORROWED_FUNDS,
  AssetsType.UNSECURED_BORROWED_FUNDS,
  AssetsType.OTHER,
  AssetsType.OTHER_NON_LUQUID,
  AssetsType.EARNEST_MONEY,
  AssetsType.EMPLOYER_ASSISTANCE,
  AssetsType.LOT_EQUITY,
  AssetsType.RELOCATION_FUNDS,
  AssetsType.RENT_CREDIT,
  AssetsType.SWEAT_EQUITY,
  AssetsType.TRADE_EQUITY,
  AssetsType.GRANT,
];

export const AssetsRelationshipType = {
  RELATIVE: 'Relative',
  COMMUNITY_NON_PROFIT: 'CommunityNonProfit',
  FEDERAL_AGENCY: 'FederalAgency',
  STATE_AGENCY: 'StateAgency',
  LENDER: 'Lender',
  EMPLOYER: 'Employer',
  LOCAL_AGENCY: 'LocalAgency',
  RELIGIOUS_NON_PROFIT: 'ReligiousNonProfit',
  UNMARRIED_PARTNER: 'UnmarriedPartner',
  OTHER: 'Other',
};

export const CreditTypeEnum = {
  LENDER_CREDIT: 'Lender',
  SELLER_CREDIT: 'Seller',
  PURCHASE_CREDIT: 'Purchase',
  OTHER_CREDIT: 'Other',
  FHA_MI_CREDIT: 'FhaMiCredit',
};

export const PurchaseCreditType = {
  EARNEST_MONEY: 'EarnestMoney',
  EMPLOYER_ASSISTED_HOUSING: 'EmployerAssistedHousing',
  LEASE_PURCHASE_FUND: 'LeasePurchaseFund',
  SWEAT_EQUITY: 'SweatEquity',
  TRADE_EQUITY_FROM_PROPERTY_SWAP: 'TradeEquityFromPropertySwap',
  CLOSING_COSTS: 'ClosingCosts',
  LOT_EQUITY: 'LotEquity',
  RELOCATION_FUNDS: 'RelocationFunds',
};

export const PurchaseCreditSourceType = {
  BORROWER_PAID_OUTSIDE_CLOSING: 'BorrowerPaidOutsideClosing',
  BUILDER_DEVELOPER: 'BuilderDeveloper',
  EMPLOYER: 'Employer',
  FEDERAL_AGENCY: 'FederalAgency',
  LENDER: 'Lender',
  LOCAL_AGENCY: 'LocalAgency',
  NON_PARENT_RELATIVE: 'NonParentRelative',
  EMPLOYER_AFFILIATE: 'EmployerAffiliate',
  LENDER_AFFILIATE: 'LenderAffiliate',
  PARENT: 'Parent',
  REAL_ESTATE_AGENT: 'RealEstateAgent',
  STATE_AGENCY: 'StateAgency',
  UNRELATED_FRIEND: 'UnrelatedFriend',
  OTHER: 'Other',
};

export const BaseStatus = {
  YES: 'true',
  NO: 'false',
};

export const PurchaseProcessType = {
  SignedPurchasedContract: 'SignedPurchasedContract',
  SearchingOrShoppingAround: 'SearchingOrShoppingAround',
  MakingOfferOrSigningPurchaseContract: 'MakingOfferOrSigningPurchaseContract',
};

export const LoanStatus = {
  TBD_BUNDLE_ISSUED: 'TbdBundleIssued',
  PREQUAL_STARTED: 'PrequalStarted',
  DID_NOT_PREQUAL: 'DidNotPrequal',
  APPLICATION_STARTED: 'ApplicationStarted',
  APPLICATION_COMPLETED_NO_RATES: 'ApplicationCompletedNoRates',
  APPLICATION_COMPLETED_HAS_RATES: 'ApplicationCompletedHasRates',
  DISCLOSURES_GENERATED: 'DisclosuresGenerated',
  DISCLOSURES_SIGNED_PARTIALLY: 'DisclosuresSignedPartially',
  DISCLOSURES_COMPLETED: 'DisclosuresCompleted',
  TASKS_REMAINING: 'TasksRemaining',
  PROCESSING: 'Processing',
  UNDEFINED: 'Undefined',
  LO_ASSIGNMENT_ERROR: 'LoAssignmentError',
  UW_APPROVED: 'UWApproved',
  CLEAR_TO_CLOSE: 'ClearToClose',
  SCHEDULED: 'Scheduled',
  CLOSED: 'Closed',
  FUNDED: 'Funded',
  UW_SUSPENDED: 'UWSuspended',
  APPROVED_NOT_ACCEPTED: 'ApprovedNotAccepted',
  WITHDRAWN: 'Withdrawn',
  UW_DENIED: 'UWDenied',
  CLOSED_FOR_INCOMPLETENESS: 'ClosedForIncompleteness',
  UW: 'UW',
  ABNORMAL_NOT_SCHEDULED: 'AbnormalNotScheduled',
  LEGACY_SYSTEM_LOAN: 'LegacySystemLoan',
  CANCELED: 'LoanCancelled',
  LEAD: 'Lead',
};

export const BorrowerType = {
  PRIMARY: 'Primary',
  SPOUSE: 'Spouse',
  COBORROWER: 'CoBorrower',
};

export const BorrowerTypeLabel = {
  [BorrowerType.PRIMARY]: 'Primary',
  [BorrowerType.SPOUSE]: 'Spouse',
  [BorrowerType.COBORROWER]: 'Co-Borrower',
};

export const SuffixType = {
  // TODO: "None" will be removed from suffix enum after BE refactoring, added for backward compatibility
  NONE: 'None',
  II: 'II',
  III: 'III',
  IV: 'IV',
  JR: 'Jr.',
  SR: 'Sr.',
};

export const MaritalStatusType = {
  MARRIED: 'Married',
  UNMARRIED: 'Unmarried',
  SEPARATED: 'Separated',
};

export const MannerHeldType = {
  SOLE_OWNERSHIP: 'SoleOwnership',
  JOINT_TENANCY: 'JointTenancy',
  TENANCY_IN_COMMON: 'TenancyInCommon',
  TENANTS_BY_ENTIRELY: 'TenantsByEntirely',
  LIFE_ESTATE: 'LifeEstate',
  OTHER: 'Other',
  INDIVIDUAL: 'Individual',
  LLC: 'LLC',
  CORPORATION: 'Corporation',
  TRUST: 'Trust',
  I_DO_NOT_KNOW: 'IDoNotKnow',
};

export const LESection = {
  ORIGINATION_CHARGES: 'OriginationCharges',
  SERVICES_YOU_CANNOT_SHOP_FOR: 'ServicesYouCannotShopFor',
  SERVICES_YOU_CAN_SHOP_FOR: 'ServicesYouCanShopFor',
  RECORDING_FEES: 'RecordingFees',
  TRANSFER_TAXES: 'TransferTaxes',
  PREPAIDS: 'Prepaids',
  OTHER: 'Other',
};

export const LESectionCDType = {
  [LESection.ORIGINATION_CHARGES]: 'A',
  [LESection.SERVICES_YOU_CANNOT_SHOP_FOR]: 'B',
  [LESection.SERVICES_YOU_CAN_SHOP_FOR]: 'C',
  [LESection.RECORDING_FEES]: 'E',
  [LESection.TRANSFER_TAXES]: 'E',
  [LESection.PREPAIDS]: 'F',
  [LESection.OTHER]: 'H',
};

export const LESectionName = {
  [LESection.ORIGINATION_CHARGES]: 'Origination Charges',
  [LESection.SERVICES_YOU_CANNOT_SHOP_FOR]: 'Services Borrower Did Not Shop For',
  [LESection.SERVICES_YOU_CAN_SHOP_FOR]: 'Services Borrower Did Shop For',
  [LESection.RECORDING_FEES]: 'Taxes and Other Government Fees',
  [LESection.TRANSFER_TAXES]: 'Taxes and Other Government Fees',
  [LESection.PREPAIDS]: 'Prepaids',
  [LESection.OTHER]: 'Other',
};

export const OccupancyType = {
  PRIMARY_RESIDENCE: 'PrimaryResidence',
  SECOND_HOME: 'SecondHome',
  INVESTMENT_PROPERTY: 'InvestmentProperty',
};

export const OccupancyTypeLabel = {
  [OccupancyType.PRIMARY_RESIDENCE]: 'Primary Residence',
  [OccupancyType.SECOND_HOME]: 'Second Home',
  [OccupancyType.INVESTMENT_PROPERTY]: 'Investment Property',
};

export const TitleHoldingType = {
  BY_YOURSELF: 'ByYourself',
  WITH_YOUR_SPOUSE: 'WithYourSpouse',
  WITH_ANOTHER_PERSON: 'WthAnotherPerson',
};

export const ClosingInTrustType = {
  INTER_VIVOS_TRUST: 'InterVivosTrust',
  LAND_TRUST: 'LandTrust',
};

export const HousingType = {
  OWN: 'Own',
  RENT: 'Rent',
  LIVING_WITH_RELATIVE: 'LivingWithRelative',
};

export const RentCalculatedPurposeType = {
  LEASE: 'Lease',
  TAX_RETURN: 'TaxReturn',
};

export const PropertyStatusType = {
  SOLD: 'Sold',
  PENDING_SALE: 'PendingSale',
  RETAINED: 'Retained',
};

export const LoanPurposeType = {
  REFINANCE: 'Refinance',
  PURCHASE: 'Purchase',
  ADDITIONAL_LOAN: 'AdditionalLoan',
  CHATTEL_LOAN: 'ChattelLoan',
};

export const HelocPurposeType = {
  HOME_PURCHASE: 'HomePurchase',
  REFINANCE: 'Refinance',
  HOME_IMPROVEMENT: 'HomeImprovement',
  DEBT_CONSOLIDATION: 'DebtConsolidation',
  OTHER: 'Other',
};

export const helocPurposeOptions = [
  { name: 'Home Purchase', value: HelocPurposeType.HOME_PURCHASE },
  { name: 'Refinance', value: HelocPurposeType.REFINANCE },
  { name: 'Home Improvement', value: HelocPurposeType.HOME_IMPROVEMENT },
  { name: 'Debt Consolidation', value: HelocPurposeType.DEBT_CONSOLIDATION },
  { name: 'Other', value: HelocPurposeType.OTHER },
];

export const CompensationType = {
  BORROWER_PAID: 'BorrowerPaid',
  LENDER_PAID: 'LenderPaid',
};

export const TransactionDetailType = {
  CONVERSATION_OF_CONTRACT_FOR_DEED_OR_LAND_CONTRACT: 'ConversionOfContractForDeedOrLandContract',
  RENOVATION: 'Renovation',
  CONSTRUCTION_CONVERSATION_OR_TO_PERMANENT: 'ConstructionConversionOrConstructionToPermanent',
};

export const TransactionDetailClosingType = {
  SINGLE_CLOSING: 'SingleClosing',
  TWO_CLOSING: 'TwoClosing',
};

export const GovernmentRefinancePurposeType = {
  FULL_DOCUMENTATION: 'FullDocumentation',
  INTEREST_RATE_REDUCTION_REFINANCE_LOAN: 'InterestRateReductionRefinanceLoan',
  STREAM_LINE_WITHOUT_APPRAISAL: 'StreamlineWithoutAppraisal',
};

export const SalaryType = {
  BASE_SALARY: 'grossMonthlyBaseSalary',
  MONTHLY_BONUS: 'grossMonthlyBonus',
  MONTHLY_COMMISSION: 'grossMonthlyCommission',
  MONTHLY_OVERTIME: 'grossMonthlyOvertime',
  MONTHLY_OTHER: 'grossMonthlyOtherIncome',
  MILITARY_BASE_PAY: 'militaryBasePay',
  MILITARY_CLOTHES_ALLOWANCE: 'militaryClothesAllowance',
  MILITARY_COMBAT_PAY: 'militaryCombatPay',
  MILITARY_FLIGHT_PAY: 'militaryFlightPay',
  MILITARY_HAZARD_PAY: 'militaryHazardPay',
  MILITARY_OTHER: 'militaryOther',
  MILITARY_OVERSEAS_PAY: 'militaryOverseasPay',
  MILITARY_PROP_PAY: 'militaryPropPay',
  MILITARY_RATIONS_ALLOWANCE: 'militaryRationsAllowance',
  MILITARY_VARIABLE_HOUSING_ALLOWANCE: 'militaryVariableHousingAllowance',
  MILITARY_QUARTERS_ALLOWANCE: 'militaryQuartersAllowance',
  CONTRACT_BASIS: 'contractBasis',
};

export const AdditionalLoanSecondMortgageType = {
  SECOND_MORTGAGE: 'SecondMortgage',
  HELOC: 'HELOC',
};

export const LoanTypePurposeType = {
  BPL: 'BPL',
  RESIDENTIAL: 'Residential',
};

export const DeliveryStatus = {
  SENT: 'Sent',
  SIGNED: 'Signed',
  OPENED: 'Opened',
  CANCELED: 'Canceled',
  // FH-1394: an email/portal recipient generated but not yet dispatched (null SentDate). Shown instead of a
  // premature "Sent" until the real dispatch event fires.
  NOT_SENT: 'NotSent',
};

export const NewFormDeliveryStatus = {
  SentToRecipient: 'SentToRecipient',
  NotificationOpened: 'NotificationOpened',
  ReadByRecipient: 'ReadByRecipient',
  ReadByRecipientWithNotificationOpened: 'ReadByRecipientWithNotificationOpened',
  SignedByRecipient: 'SignedByRecipient',
  SignedByRecipientWithNotificationOpened: 'SignedByRecipientWithNotificationOpened',
  Signable: 'Signable',
  Scheduled: 'Scheduled',
};

// Leading segment of the dotted status discriminators Skynet.Forms serializes — NOT whole status
// values, so these never match a status by equality. The request lane spells them
// `Cancelled.{AfterSending,AfterAcknowledging,AfterScheduling}` / `InProgress.{AfterSending,...}` /
// `Completed.{OnSending,OnAcknowledging,OnSigning}`, the recipient lane `CancelledBySystem.{...}`.
export const FormDeliveryStatusFamily = {
  CANCELLED: 'Cancelled',
  CANCELLED_BY_SYSTEM: 'CancelledBySystem',
  IN_PROGRESS: 'InProgress',
  COMPLETED: 'Completed',
};

export const FormDeliveryReadinessStatus = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const NewFormDeliveryStatusToOldMap = {
  [NewFormDeliveryStatus.SentToRecipient]: DeliveryStatus.SENT,
  [NewFormDeliveryStatus.NotificationOpened]: DeliveryStatus.SENT,
  [NewFormDeliveryStatus.ReadByRecipient]: DeliveryStatus.OPENED,
  [NewFormDeliveryStatus.ReadByRecipientWithNotificationOpened]: DeliveryStatus.OPENED,
  [NewFormDeliveryStatus.SignedByRecipient]: DeliveryStatus.SIGNED,
  [NewFormDeliveryStatus.SignedByRecipientWithNotificationOpened]: DeliveryStatus.SIGNED,
};

export const RecipientRequestType = {
  Signable: 'Signable',
  NonSignable: 'NonSignable',
  PhysicalMail: 'PhysicalMail',
};

// Mirrors Skynet.Forms' FormDeliveryChannel enum (Email | Mail).
export const FormDeliveryChannelType = {
  EMAIL: 'Email',
  MAIL: 'Mail',
};

// Canonical FE vocabulary for a PhysicalMail recipient request's delivery progress. Deliberately
// NOT the raw `status.type` discriminator the API serializes (e.g. `InTransitByMail`,
// `DeliveredByMail`) — that VO naming is an implementation detail of Skynet.Forms'
// IFormDeliveryRecipientRequestStatus.IPhysicalMail hierarchy. `getFormattedRecipientsRequests`
// normalizes into these values so the rest of the FE never depends on the backend's internal
// discriminator spelling.
export const PhysicalMailDeliveryStatus = {
  AWAITING_DISPATCH: 'AwaitingDispatch',
  MAILED: 'Mailed',
  IN_TRANSIT: 'InTransit',
  DELIVERED: 'Delivered',
  RETURNED_TO_SENDER: 'ReturnedToSender',
  MAIL_FAILED: 'MailFailed',
};

export const FormDeliveryRecipientType = {
  PRIMARY_BORROWER: 'PrimaryBorrower',
  CO_BORROWER: 'CoBorrower',
};

export const FormDeliveryAcknowledgmentRule = {
  MANDATORY_ON_READING_BY_ALL_BORROWERS: 'Mandatory.OnReadingByAllBorrowers',
  MANDATORY_ON_READING_BY_PRIMARY_BORROWER: 'Mandatory.OnReadingByPrimaryBorrower',
  NONE: 'None',
};

export const TypeOfCredit = {
  REVOLVING: 'Revolving',
  INSTALLMENT: 'Installment',
  MORTGAGE: 'Mortgage',
  LEASE: 'Lease',
  LINE_OF_CREDIT: 'LineOfCredit',
  OPEN_30_DAY: 'Open30Day',
  COLLECTION: 'Collection',
  OTHER: 'Other',
};

export const LoanType = {
  FHA: 'FHA',
  VA: 'VA',
  CONVENTIONAL: 'Conventional',
  USDA: 'USDA',
  OTHER: 'Other',
  BPL: 'BPL',
  JUMBO: 'Jumbo',
  NON_QM: 'NonQM',
  CONFORMING: 'Conforming',
  NON_CONFORMING: 'NonConforming',
  HELOC: 'HELOC',
  HELOAN: 'HELoan',
  PERSONAL: 'Personal',
  REVERSE_MORTGAGE: 'ReverseMortgage',
  DSCR: 'DSCR',
  CHATTEL: 'Chattel',
};

export const GovernmentRefinancePurposeLoanTypes = [LoanType.FHA, LoanType.VA, LoanType.USDA];

export const RefinancePurposeType = {
  PAYOFF_CREDIT_CARDS: 'PayOffCreditCards',
  TAKE_CASH_OUT_OF_HOME: 'TakeCashOutOfHome',
  LOWER_MONTHLY_PAYMENT: 'LowerMonthlyPayment',
  PAYOFF_LOAN_FASTER: 'PayOffLoanFaster',
  CONSOLIDATE_LOANS: 'ConsolidateLoans',
  HOME_IMPROVEMENT: 'HomeImprovement',
  FHA_SIMPLE_REFINANCE: 'FHASimpleRefinance',
  FHA_STREAMLINE_REFINANCE: 'FHAStreamlineRefinance',
  VA_RATE_REDUCTION: 'VARateReduction',
};

export const FhaRefinancePurposeTypes = [
  RefinancePurposeType.FHA_SIMPLE_REFINANCE,
  RefinancePurposeType.FHA_STREAMLINE_REFINANCE,
];

export const VaRefinancePurposeTypes = [RefinancePurposeType.VA_RATE_REDUCTION];

export const GovernmentRefinancePurposeByRefinancePurpose = {
  [RefinancePurposeType.FHA_SIMPLE_REFINANCE]: GovernmentRefinancePurposeType.FULL_DOCUMENTATION,
  [RefinancePurposeType.FHA_STREAMLINE_REFINANCE]:
    GovernmentRefinancePurposeType.STREAM_LINE_WITHOUT_APPRAISAL,
  [RefinancePurposeType.VA_RATE_REDUCTION]:
    GovernmentRefinancePurposeType.INTEREST_RATE_REDUCTION_REFINANCE_LOAN,
};

export const TangibleNetBenefitReasonType = {
  AVOIDING_FORECLOSURE: 'AvoidingForeclosure',
  BENEFICIAL_CHANGE_IN_AMORTIZATION_PERIOD: 'BeneficialChangeInAmortizationPeriod',
  BONA_FIDE_PERSONAL_NEED: 'BonaFidePersonalNeed',
  CHANGE_FROM_ADJUSTABLE_TO_FIXED_RATE: 'ChangeFromAdjustableToFixedRate',
  CONSOLIDATION_OF_DEBT: 'ConsolidationOfDebt',
  ELIMINATING_BALLOON_PAYMENT: 'EliminatingBalloonPayment',
  ELIMINATING_NEGATIVE_AMORTIZATION: 'EliminatingNegativeAmortization',
  ELIMINATING_PRIVATE_MORTGAGE_INSURANCE: 'EliminatingPrivateMortgageInsurance',
  LOWER_INTEREST_RATE: 'LowerInterestRate',
  LOWER_MONTHLY_PAYMENT: 'LowerMonthlyPayment',
  RECEIPT_OF_CASH: 'ReceiptOfCash',
  OTHER: 'Other',
};

export const TANGIBLE_NET_BENEFIT_STATES = ['RI', 'MN', 'MD', 'WV', 'MA'];

export const LiabilityExpenseType = {
  ALIMONY: 'Alimony',
  CHILD_SUPPORT: 'ChildSupport',
  SEPARATE_MAINTENANCE: 'SeparateMaintenance',
  JOB_RELATED_EXPENSES: 'JobRelatedExpenses',
  OTHER: 'Other',
};

export const AttachmentType = {
  ATTACHED: 'Attached',
  DETACHED: 'Detached',
};

export const PropertyType = {
  CONDO: 'Condo',
  COOP: 'Coop',
  MULTI_UNIT: 'MultiUnit',
  SINGLE_FAMILY: 'SingleFamily',
  TOWNHOUSE: 'Townhouse',
  MANUFACTURED_HOME: 'ManufacturedHome',
  LAND: 'Land',
  PUD: 'Pud',
};

export const ManufacturedHomeType = {
  SINGLE_WIDE: 'SingleWide',
  DOUBLE_WIDE: 'DoubleWide',
  TRIPLE_WIDE: 'TripleWide',
};

export const SitePlacement = {
  OWNED_NO_LIEN: 'OwnedNoLien',
  LEASED_PRIVATE_PROPERTY: 'LeasedPrivateProperty',
  FAMILY_LAND_NO_RENT: 'FamilyLandNoRent',
  COMMUNITY_PARK: 'CommunityPark',
  RESERVATION: 'Reservation',
  OWNED_LAND_CONTRACT: 'OwnedLandContract',
};

// Mirrors Lender.Optimus.LoanApplications.PurposeOfChattel (ChattelDetails.PurposeOfChattel).
export const PurposeOfChattel = {
  PURCHASE: 'Purchase',
  REFINANCE: 'Refinance',
};

// Mirrors Lender.Optimus.LoanApplications.ChattelHomeCondition. Repo is not offered in the form, but an
// application created elsewhere (POS) can carry it.
export const ChattelHomeCondition = {
  NEW: 'New',
  USED: 'Used',
  REPO: 'Repo',
};

// Mirrors Lender.Optimus.LoanApplications.ChattelValueSource: where a chattel home value comes from.
export const ChattelValueSource = {
  APPRAISAL: 'Appraisal',
  NADA: 'Nada',
  OTHER: 'Other',
};

export const AddressField = {
  CITY: 'city',
  STREET: 'street',
  ZIP: 'zip',
  COUNTY: 'county',
  STATE: 'state',
  LATITUDE: 'latitude',
  LONGITUDE: 'longitude',
  UNIT: 'unit',
  IS_OUTSIDE_USA: 'isOutsideUsa',
  SUITE: 'suite',
};

const OMITTED_ADDRESS_FIELDS = [AddressField.LATITUDE, AddressField.LONGITUDE, 'fullAddress'];

export const ADDRESS_FIELDS_TO_COUNT = Object.values(AddressField).filter(
  val => !OMITTED_ADDRESS_FIELDS.includes(val),
);

export const PricingWorkbenchPropertyType = {
  CONDO: 'Condo',
  COOP: 'Coop',
  SINGLE_FAMILY: 'SingleFamily',
  TOWNHOUSE: 'Townhouse',
  MANUFACTURED_HOME: 'ManufacturedHome',
  MULTI_UNIT2: 'MultiUnit2',
  MULTI_UNIT3: 'MultiUnit3',
  MULTI_UNIT4: 'MultiUnit4',
  PUD: 'Pud',
};

export const CreditReportStatus = {
  INITIAL: 'Initial',
  FETCHING: 'Fetching',
  OK: 'Ok',
  ERROR: 'Error',
  SERVER_IS_UNAVAILABLE: 'ServerIsUnavailable',
  ERROR_BUT_OK: 'ErrorButOk',
};

export const SignatureRequestStatus = {
  BORROWERS_PENDING: 'BorrowersPending',
  LOAN_OFFICER_PENDING: 'LoanOfficerPending',
  COMPLETED: 'Completed',
  // A non-signable package that completed on sending or acknowledgment. Distinct from COMPLETED so
  // nothing downstream can read a delivery that collected no signature as an e-signed one.
  DELIVERED: 'Delivered',
  CANCELED: 'Canceled',
  SCHEDULED: 'ScheduledAutoDelivery',
  SIGNED_WITHOUT_DOCUMENT: 'SignedWithoutDocument',
};

export const CitizenshipStatusType = {
  CITIZEN: 'Citizen',
  PERMANENT_RESIDENT_ALIEN: 'PermanentResidentAlien',
  NON_PERMANENT_RESIDENT_ALIEN: 'NonPermanentResidentAlien',
  ALIEN_FOREIGN_NATIONAL: 'AlienForeignNational',
};

export const LoanChangesHistoryState = {
  APPLIED: 'Applied',
  PENDING: 'Pending',
  CANCELLED: 'Cancelled',
};

export const AsianRaceSubType = {
  ASIAN_INDIAN: 'isAsianIndian',
  CHINESE: 'isChinese',
  FILIPINO: 'isFilipino',
  JAPANESE: 'isJapanese',
  KOREAN: 'isKorean',
  VIETNAMESE: 'isVietnamese',
  OTHER_ASIAN: 'isOtherAsian',
};

export const NativeHawaiianOrOtherRaceSubType = {
  NATIVE_HAWAIIAN: 'isNativeHawaiian',
  GUAMANIAN_OR_CHAMORRO: 'isGuamanianOrChamorro',
  SAMOAN: 'isSamoan',
  OTHER_PACIFIC_ISLANDER: 'isOtherPacificIslander',
};

export const HispanicOrLatinoEthnicitySubType = {
  MEXICAN: 'isMexican',
  CUBAN: 'isCuban',
  PUERTO_RICAN: 'isPuertoRican',
  OTHER_HISPANIC_OR_LATINO: 'isOtherHispanicOrLatinoEthnicity',
};

export const EthnicityType = {
  HISPANIC_OR_LATINO: 'isHispanicOrLatino',
  NON_HISPANIC_OR_LATINO: 'isNonHispanicOrLatino',
  NOT_PROVIDED: 'isEthnicityNotProvided',
};

export const RaceType = {
  AMERICAN_INDIAN_OR_ALASKA_NATIVE: 'isAmericanIndianOrAlaskaNative',
  ASIAN: 'isAsian',
  HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER: 'isNativeHawaiianOrOtherPacificIslander',
  AFRICAN_AMERICAN: 'isBlackOrAfricanAmerican',
  WHITE: 'isWhite',
  NOT_PROVIDED: 'isRaceNotProvided',
};

export const DemographicInformationProvisionType = {
  FACE_TO_FACE_INTERVIEW: 'FaceToFaceInterview',
  TELEPHONE_INTERVIEW: 'TelephoneInterview',
  FAX_OR_MAIL: 'FaxOrMail',
  EMAIL_OR_INTERNET: 'EmailOrInternet',
};

export const CounselingFormat = {
  CLASSROOM: 'Classroom',
  HOME_STUDY: 'HomeStudy',
  BORROWER_EDUCATION_NOT_REQUIRED: 'BorrowerEducationNotRequired',
  BORROWER_DID_NOT_PARTICIPATE: 'BorrowerDidNotParticipate',
  OTHER: 'Other',
};

export const HomeBuyerCounseling = {
  AMERICAN_HOMEOWNER_EDUCATION_AND_COUNSELING_INSTITUTE_APPROVED:
    'AmericanHomeownerEducationAndCounselingInstituteApproved',
  HUD_APPROVED_COUNSELING_AGENCY: 'HUDApprovedCounselingAgency',
  LENDER_TRAINED: 'LenderTrained',
  LOCAL_HOUSING_COUNSELING_AGENCY: 'LocalHousingCounselingAgency',
  MULTI_STATE_ORGANIZATION: 'MultiStateOrganization',
  NATIONAL_INTERMEDIARY: 'NationalIntermediary',
  REGIONAL_INTERMEDIARY: 'RegionalIntermediary',
  STATE_HOUSING_FINANCE_AGENCY: 'StateHousingFinanceAgency',
  SUB_GRANTEE: 'SubGrantee',
  GOVERNMENT_AGENCY: 'GovernmentAgency',
  NO_BORROWER_COUNSELING: 'NoBorrowerCounseling',
  BORROWER_DID_NOT_PARTICIPATE: 'BorrowerDidNotParticipate',
  MORTGAGE_INSURANCE_COMPANY: 'MortgageInsuranceCompany',
  NON_PROFIT_ORGANIZATION: 'NonProfitOrganization',
  OTHER: 'Other',
};

export const ProjectInformation = {
  ESTABLISHED: 'Established',
  NEW: 'New',
};

export const ProjectClassification = {
  PUD_E: 'PudE',
  PUD_F: 'PudF',
  PUD_T: 'PudT',
  CO_OP_1: 'CoOp1',
  CO_OP_2: 'CoOp2',
  CO_OP_T: 'CoOpT',
};

export const UccpAppraisalUploadStatus = {
  IN_PROGRESS: 'InProgress',
  SUCCESSFUL: 'Successful',
  NOT_SUCCESSFUL: 'NotSuccessful',
  REJECTED: 'Rejected',
  NA: 'NA',
};

export const counselingFormatOptions = [
  {
    name: 'Borrower Education Not Required',
    value: CounselingFormat.BORROWER_EDUCATION_NOT_REQUIRED,
  },
  { name: 'Borrower Did Not Participate', value: CounselingFormat.BORROWER_DID_NOT_PARTICIPATE },
  {
    name: 'Classroom',
    value: CounselingFormat.CLASSROOM,
  },
  {
    name: 'Home Study',
    value: CounselingFormat.HOME_STUDY,
  },
  {
    name: 'Other',
    value: CounselingFormat.OTHER,
  },
];

export const homeBuyerCounselingOptions = [
  {
    name: 'American Homeowner Education And Counseling Institute Approved',
    value: HomeBuyerCounseling.AMERICAN_HOMEOWNER_EDUCATION_AND_COUNSELING_INSTITUTE_APPROVED,
  },
  { name: 'Borrower Did Not Participate', value: HomeBuyerCounseling.BORROWER_DID_NOT_PARTICIPATE },
  { name: 'Government Agency', value: HomeBuyerCounseling.GOVERNMENT_AGENCY },
  {
    name: 'HUD Approved Counseling Agency',
    value: HomeBuyerCounseling.HUD_APPROVED_COUNSELING_AGENCY,
  },
  {
    name: 'Lender Trained',
    value: HomeBuyerCounseling.LENDER_TRAINED,
  },
  {
    name: 'Local Housing Counseling Agency',
    value: HomeBuyerCounseling.LOCAL_HOUSING_COUNSELING_AGENCY,
  },
  { name: 'Mortgage Insurance Company', value: HomeBuyerCounseling.MORTGAGE_INSURANCE_COMPANY },
  {
    name: 'Multi State Organization',
    value: HomeBuyerCounseling.MULTI_STATE_ORGANIZATION,
  },
  {
    name: 'National Intermediary',
    value: HomeBuyerCounseling.NATIONAL_INTERMEDIARY,
  },
  { name: 'No Borrower Counseling', value: HomeBuyerCounseling.NO_BORROWER_COUNSELING },
  { name: 'Non Profit Organization', value: HomeBuyerCounseling.NON_PROFIT_ORGANIZATION },
  {
    name: 'Regional Intermediary',
    value: HomeBuyerCounseling.REGIONAL_INTERMEDIARY,
  },
  {
    name: 'State Housing Finance Agency',
    value: HomeBuyerCounseling.STATE_HOUSING_FINANCE_AGENCY,
  },
  {
    name: 'Sub Grantee',
    value: HomeBuyerCounseling.SUB_GRANTEE,
  },
  {
    name: 'Other',
    value: HomeBuyerCounseling.OTHER,
  },
];

export const BankruptType = {
  CHAPTER_7: 'Chapter7',
  CHAPTER_11: 'Chapter11',
  CHAPTER_12: 'Chapter12',
  CHAPTER_13: 'Chapter13',
};

export const EmploymentStatusType = {
  PAST: 'Past',
  CURRENT: 'Current',
};

export const LocationType = {
  SUBURBAN: 'Suburban',
  URBAN: 'Urban',
  RURAL: 'Rural',
};

export const IncomeDocumentationType = {
  FULL_DOC: 'FullDoc',
  PERSONAL_BANK_STATEMENT_12: 'PersonalBankStatement12',
  PERSONAL_BANK_STATEMENT_24: 'PersonalBankStatement24',
  BUSINESS_BANK_STATEMENT_12: 'BusinessBankStatement12',
  BUSINESS_BANK_STATEMENT_24: 'BusinessBankStatement24',
  FORM_1099_ONE_YEAR: 'Form1099OneYear',
  FORM_1099_TWO_YEAR: 'Form1099TwoYear',
  PROFIT_AND_LOSS_12: 'ProfitAndLoss12',
  PROFIT_AND_LOSS_24: 'ProfitAndLoss24',
  ASSET_DEPLETION: 'AssetDepletion',
  WVOE: 'Wvoe',
  DSCR: 'Dscr',
  NO_RATIO: 'NoRatio',
};

export const PrepaymentPenaltyStructure = {
  NONE: 'None',
  FIXED: 'Fixed',
  DECLINING: 'Declining',
  SIX_MONTHS_INTEREST: 'SixMonthsInterest',
};

export const CreditEventType = {
  BANKRUPTCY: 'Bankruptcy',
  FORECLOSURE: 'Foreclosure',
  SHORT_SALE: 'ShortSale',
  DEED_IN_LIEU: 'DeedInLieu',
};

export const CreditEventSource = {
  PUBLIC_RECORD: 'PublicRecord',
  ACCOUNT_STATUS: 'AccountStatus',
  RATING: 'Rating',
  COMMENT: 'Comment',
};

export const CreditEventReliability = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

export const CreditEventsRefreshStatus = {
  REPARSED: 'Reparsed',
  UNCHANGED: 'Unchanged',
  STORED_RESPONSE_NOT_FOUND: 'StoredResponseNotFound',
  NO_CREDIT_REPORT: 'NoCreditReport',
  BORROWER_NOT_ON_CREDIT_REPORT: 'BorrowerNotOnCreditReport',
};

export const PropertyRightsEstateHeld = {
  FEE_SIMPLE: 'FeeSimple',
  LEASE_HOLD: 'LeaseHold',
};

export const SourceOfFundsType = {
  COMMUNITY_NON_PROFIT: 'CommunityNonProfit',
  EMPLOYER: 'Employer',
  FEDERAL_AGENCY: 'FederalAgency',
  LENDER: 'Lender',
  LOCAL_AGENCY: 'LocalAgency',
  OTHER: 'Other',
  PARENT: 'Parent',
  PROPERTY_SELLER: 'PropertySeller',
  RELATIVE: 'Relative',
  RELIGIOUS_NON_PROFIT: 'ReligiousNonProfit',
  STATE_AGENCY: 'StateAgency',
  UNMARRIED_PARTNER: 'UnmarriedPartner',
  UNRELATED_FRIEND: 'UnrelatedFriend',
  GIFT_OF_PROPERTY_EQUITY: 'GiftOfPropertyEquity',
};

export const PaymentExclusionReasonType = {
  CO_SIGNED_LOAN: 'CoSignedLoan',
  COURT_ORDERED: 'CourtOrdered',
  DEFERRED: 'Deferred',
  DUPLICATE: 'Duplicate',
  LESS_THAN_10_MTHS_REMAIN: 'LessThan10MthsRemain',
  NOT_THE_BORROWERS: 'NotTheBorrowers',
  PAID_BY_EMPLOYER: 'PaidByEmployer',
  PAID_BY_OTHERS: 'PaidByOthers',
  PAID_IN_FULL: 'PaidInFull',
  PROPERTY_BUYOUT: 'PropertyBuyout',
  SECURED: 'Secured',
  TO_BE_PAID: 'ToBePaid',
  OTHER: 'Other',
};

export const LienPriorityType = {
  FIRST: 'First',
  SECOND: 'Second',
  THIRD: 'Third',
  FOURTH: 'Fourth',
  OTHER: 'Other',
};

export const DeferredType = {
  DEFERRED_FULLY_FORGIVEN: 'DeferredFullyForgiven',
  DEFERRED_NOT_FULLY_FORGIVEN: 'DeferredNotFullyForgiven',
};

export const AusType = {
  DU: 'Du',
  LP: 'Lp',
  DU_LP: 'DuAndLp',
  DU_LP_PENDING_CHANGES: 'DuAndLpPendingChanges',
};

export const AusDecisionType = {
  LPURLA: 'LPURLA',
  DO: 'DO',
};

export const AusOperationStatus = {
  NEW: 'New',
  IN_PROGRESS: 'InProgress',
  CANCELLED: 'Cancelled',
  ERROR: 'Error',
  COMPLETED: 'Completed',
};

export const AusOperationStatusToNumberStatusMap = {
  1: AusOperationStatus.NEW,
  2: AusOperationStatus.IN_PROGRESS,
  3: AusOperationStatus.CANCELLED,
  4: AusOperationStatus.ERROR,
  5: AusOperationStatus.COMPLETED,
};

export const AusTypeSwitchStatus = {
  CREATED: 'Created',
  COMPLETED: 'Completed',
};

export const LoanApplicationSignalREventType = {
  AUS_SUBMIT_OPERATION_CHANGED: 'AusSubmitOperationChanged',
  AUS_TYPE_SWITCH_STATUS_CHANGED: 'AusTypeSwitchStatusChanged',
  MESSAGE_RAISED: 'MessageRaised',
  CONDITIONS_MODIFIED: 'LoanConditionsModified',
  CONDITION_CREATED: 'LoanConditionCreated',
  AUTO_LOCKED_FIELDS_CHANGED: 'AutoLockedFieldsChanged',
  PRICING_STATUS_CHANGED: 'PricingStatusChanged',
  PENDING_CHANGES_SAVED: 'PendingChangesSaved',
  LOAN_DATA_CHANGED: 'LoanDataChanged',
  LOAN_HEADER_REFRESH_REQUESTED: 'LoanHeaderRefreshRequested',
};

export const LoanDataChangedReason = {
  PENDING_CHANGES_SAVED: 'pendingChangesSaved',
  PENDING_CHANGES_CANCELLED: 'pendingChangesCancelled',
  DRAWER_SAVED: 'drawerSaved',
};

export const LoanApplicationSignalRMessageType = {
  CONNECT_TO_LOAN_EDITOR: 'connectToLoanEditor',
  DISCONNECT_FROM_LOAN_EDITOR: 'disconnectFromLoanEditor',
};

export const ExtensionType = {
  RETAIN_RATE: 'RetainRate',
  REPRICE_LOAN: 'RepriceLoan',
};

export const PropertyMortgageType = {
  FIRST: 'firstMortgage',
  SECOND: 'secondMortgage',
};

export const PropertyMortgageLiabilityType = {
  FIRST: `${PropertyMortgageType.FIRST}liabilityId`,
  SECOND: `${PropertyMortgageType.SECOND}liabilityId`,
};

export const LoanApplicationRestrictionMessage = {
  NO_PERMISSIONS_TO_EDIT_LOAN: 'You do not have permission to edit Loan.',
  NO_PERMISSIONS_TO_CHANGE_PROGRAM:
    'Please contact your manager, you are not authorized to make changes to the program/rate.',
  CURRENT_USER_CANT_CHANGE_LOAN: 'The loan can be only edited after the Loan officer save changes',
  CURRENT_USER_CANT_CHANGE_LEAD: 'The lead can be only edited after the Loan officer save changes',
};

export const BorrowerHomeownershipEducationFormat = {
  ATTENDED_WORKSHOP_IN_PERSON: 'AttendedWorkshopInPerson',
  COMPLETED_WEB_BASED_WORKSHOP: 'CompletedWebBasedWorkshop',
};

export const BorrowerHousingCounselingFormat = {
  FACE_TO_FACE: 'FaceToFace',
  TELEPHONE: 'Telephone',
  INTERNET: 'Internet',
  HYBRID: 'Hybrid',
};

export const LanguagePreference = {
  ENGLISH: 'English',
  CHINESE: 'Chinese',
  KOREAN: 'Korean',
  SPANISH: 'Spanish',
  TAGALOG: 'Tagalog',
  VIETNAMESE: 'Vietnamese',
  OTHER: 'Other',
  I_DO_NOT_WITH_TO_RESPOND: 'IdoNotWishToRespond',
};

// TODO: replace LanguagePreferenceV2 with LanguagePreference after 1003/declarations will be fully deleted
export const LanguagePreferenceV2 = {
  ENGLISH: 'English',
  CHINESE: 'Chinese',
  KOREAN: 'Korean',
  SPANISH: 'Spanish',
  TAGALOG: 'Tagalog',
  VIETNAMESE: 'Vietnamese',
  OTHER: 'Other',
  I_DO_NOT_WITH_TO_RESPOND: 'DoesntMatter',
};

export const LoanHeaderViewType = {
  PRIMARY: 'Primary',
  SECONDARY: 'Secondary',
};

export const HeaderViewType = {
  LOAN: 'Loan',
  CONTACT: 'Contact',
  LEAD: 'Lead',
  SUB_COMPANY: 'SubCompany',
};

export const WidgetType = {
  LOAN_ROLES: {
    id: '00000000-0000-0000-0000-000000000001',
    name: 'Loan Roles',
  },
  LOAN_NUMBER: {
    id: '00000000-0000-0000-0000-000000000002',
    name: 'Loan Number',
  },
  MILESTONE_STATUS: {
    id: '00000000-0000-0000-0000-000000000003',
    name: 'Milestone Status',
  },
  ARCHIVED: {
    // Lowercase to match .NET Guid JSON serialization. Sibling lookups
    // in HeaderViewItemChip.utils.js normalize via _.toLower() — mirror
    // that convention. (Other widget IDs are all-zero so case is invisible.)
    id: '67c155ab-dbd0-4f0f-bc40-658a4acd3a83',
    name: 'Archived',
  },
  TEST_LOAN: {
    id: '5b12af8c-ac85-4a28-b7d9-c95de3f7bd3c',
    name: 'Test Loan',
  },
};

export const TAG_WIDGET_IDS = [WidgetType.ARCHIVED.id, WidgetType.TEST_LOAN.id];

export const loanStatus = 'loan status';

export const lpAusStatusFieldName = 'lp';
export const duAusStatusFieldName = 'du';
export const ausTypeFieldName = 'aus type';
export const waiverAusStatusFieldName = 'waiver';
export const lqaAusStatusFieldName = 'lqa';
export const duUrlaAusStatusFieldName = 'duurla';
export const lpUrlaAusStatusFieldName = 'lpurla';
export const doAusStatusFieldName = 'do';
export const lpaAusStatusFieldName = 'lpa';

export const CreditRatingStatus = {
  EXCELLENT: 'Excellent',
  GOOD: 'Good',
  POOR: 'Poor',
  AVERAGE: 'Average',
  BELOW_AVERAGE: 'BelowAverage',
};

export const LoanApplicationEscrowRequirementChangesMessage = {
  ESCROW_REQUIRED: 'Due to a change in loan parameters, escrow is now required for this loan',
  ESCROW_NOT_REQUIRED:
    'Due to a change in loan parameters, escrow is no longer required for this loan. You may change the escrow selection on the Loan tab',
};

export const ClosingLocation = {
  PRIMARY_RESIDENCE: 'PrimaryResidence',
  TITLE_ESCROW_COMPANY: 'TitleEscrowCompany',
};

export const ChecklistOverrideType = {
  OVERRIDE: 'Override',
  REMOVE_OVERRIDE: 'RemoveOverride',
};

export const AttendantCompanyTypeEnum = {
  TITLE: 'Title',
  ESCROW: 'Escrow',
  SELLER_ATTORNEY: 'SellerAttorney',
  BUYER_ATTORNEY: 'BuyerAttorney',
  BROKER: 'Broker',
  DEALER: 'Dealer',
  INVESTOR: 'Investor',
};

export const SellerAdjustmentType = {
  PAID: 'Paid',
  UNPAID: 'Unpaid',
};

export const OptimalBlueAdjustmentType = {
  PRICE: 'Price',
  SRP: 'SRP',
  RATE: 'Rate',
};

export const FrequencyType = {
  ANNUAL: 'Annual',
  BIANNUAL: 'Biannual',
  TRIANNUAL: 'Triannual',
  QUATERLY: 'Quarterly',
  MONTHLY: 'Monthly',
};

export const FundingInformationType = {
  WET: 'Wet',
  DRY: 'Dry',
};

export const ClosingDocumentType = {
  SHIP_RECORDED_MORTGAGE_PLUS_RIDERS: 'ShipRecordedMortgagePlusRiders',
  SHIP_FINAL_TITLE_POLICY: 'ShipFinalTitlePolicy',
};

export const DeedRestrictionType = {
  SURVIVE: 'Survive',
  TERMINATE: 'Terminate',
};

export const NationalHousingActSection = {
  SECTION_203B_FEDERAL_MORTGAGE: 'Section203BFederalMortgage',
  SECTION_203B251_CONDOMINIUM_ARM: 'Section203B251CondominiumARM',
  SECTION_203K_FHA_REHABILITATION_LOAN_INSURANCE: 'Section203KFHARehabilitationLoanInsurance',
  SECTION_203K251_PURCHASE_REHAB_ARM: 'Section203k251PurchaseRehabARM',
  SECTION_234C_FHA_MORTGAGE_INSURANCE_FOR_CONDOMINIUM_UNITS:
    'Section234CFHAmortgageInsuranceForCondominiumUnits',
  SECTION_234C251_CONDOMINIUM_ARM: 'Section234C251CondominiumARM',
};

export const currencyOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
};

export const accountingFormatOptions = {
  isAccountingFormat: true,
};

export const fixedTwoFractionDigitsCurrencyOptions = {
  ...currencyOptions,
  minimumFractionDigits: 2,
};

export const fixedTwoFractionDigitsExceptIntegerCurrencyOptions = {
  ...fixedTwoFractionDigitsCurrencyOptions,
  omitFractionIfInteger: true,
};

export const ADDRESS_COUNTRY_TYPE = {
  NON_USA: 'NonUSA',
  USA: 'USA',
};

export const USCountryOption = [{ name: 'United States ', value: ADDRESS_COUNTRY_TYPE.USA }];

export const LoanEngineType = {
  NO_PPE: 'NoPpe',
  GENERAL: 'General',
};

export const MiProductType = {
  MONTHLY: 'Monthly',
  SINGLE_PREMIUM: 'SinglePremium',
  SPLIT_PREMIUM: 'SplitPremium',
  PREPAID_MONTHLY: 'PrepaidMonthly',
};

export const MiPaymentType = {
  BORROWER_PAID: 'BorrowerPaid',
  LENDER_PAID: 'LenderPaid',
};

export const MiRenewalType = {
  CONSTANT: 'Constant',
  DECLINING: 'Declining',
};

export const MiCompanyType = {
  ENACT: 'Enact',
  RADIAN: 'Radian',
  ESSENT: 'Essent',
  NATIONAL_MI: 'NationalMI',
  ARCH_MI: 'ArchMI',
  MGIC: 'MGIC',
  OTHER: 'Other',
};

export const MiApplicationType = {
  DELEGATED: 'Delegated',
  NON_DELEGATED: 'NonDelegated',
};

export const MiOrderStatus = {
  SUBMITTED: 'Submitted',
  DECISION_RECEIVED: 'DecisionReceived',
  ACTIVATED: 'Activated',
  CANCELLED: 'Cancelled',
  FAILED: 'Failed',
};

export const MiDecisionType = {
  APPROVED: 'Approved',
  APPROVED_AFTER_REEVALUATION: 'ApprovedAfterReevaluation',
  CONDITIONED_APPROVAL: 'ConditionedApproval',
  SUSPENDED: 'Suspended',
  DECLINED: 'Declined',
};

export const DownPaymentSourceType = {
  CHECKING: 'Checking',
  SAVINGS: 'Savings',
  CERTIFICATE_OF_DEPOSIT: 'CertificateOfDeposit',
  GIFT: 'Gift',
  RETIREMENT_ACCOUNT: 'RetirementAccount',
  STOCKS_BONDS_MUTUAL_FUNDS: 'StocksBondsMutualFunds',
  OTHER: 'Other',
};

export const ServicingType = {
  SERVICE_LOAN: 'ServiceLoan',
  TRANSFER: 'Transfer',
};

export const TRUST_ENTITY_NAMES_NUMBER = 4;

export const EscrowRequirementType = {
  NOT_ALLOWED: 'NotAllowed',
  OPTIONAL: 'Optional',
  REQUIRED: 'Required',
};

export const LoanOptionSelectorType = {
  BY_LOAN_OPTION_ID: 'ByLoanOptionId',
  BY_PRICING: 'ByPricing',
};

export const FeeCicReasonType = {
  WITHOUT_REASON: 'WithoutReason',
  WITH_CIC_REASON: 'WithCicReasons',
};

export const EntitlementCodeFromCoeType = {
  REGULAR: 'Regular',
  RESERVERS_NATIONAL_GUARD: 'ReservesNationalGuard',
  SURVIVING_SPOUSE_ACTIVE_DUTY_DEATH: 'SurvivingSpouseActiveDutyDeath',
  SURVIVING_SPOUSE_SERVICE_CONNECTED_DEATH: 'SurvivingSpouseServiceConnectedDeath',
  ACTIVE_DUTY: 'ActiveDuty',
  UNREMARRIED_SURVIVING_SPOUSE_OF_POW_MIA: 'UnremarriedSurvivingSpouseOfPowMia',
  SPOUSE_OF_ACTIVE_DUTY_SERVICE_MEMBER: 'SpouseOfActiveDutyServiceMember',
  SELECTED_RESERVE_GUARD_POST_1992: 'SelectedReserveGuardPost1992',
  OTHER: 'Other',
};

export const BranchOfServiceType = {
  ARMY: 'Army',
  NAVY: 'Navy',
  AIR_FORCE: 'AirForce',
  MARINES: 'Marines',
  COAST_GUARD: 'CoastGuard',
  SPACE_FORCE: 'SpaceForce',
};

export const MilitaryStatusType = {
  ACTIVE: 'Active',
  RESERVE_GUARD: 'ReserveGuard',
  VETERAN: 'Veteran',
  SURVING_SPOUSE: 'SurvivingSpouse',
};

export const LockExtensionsRequestType = {
  LENDER: 'Lender',
  EXTERNAL: 'External',
};

export const PricingUpdateRequestType = {
  LOCK_EXTENSION_REQUEST: 'LockExtensionRequest',
  PROFILE_CHANGE_REQUEST: 'ProfileChangeRequest',
};

export const PricingStatusConfigurationType = {
  PROGRAM_CHANGE_CONFIGURATION: 'programChangeConfiguration',
  RELOCK_CONFIGURATION: 'relockConfiguration',
  LOCK_DESK_CONFIGURATION: 'lockDeskConfiguration',
  FLOAT_DOWN_CONFIGURATION: 'floatDownConfiguration',
  PRICE_CONCESSION_CONFIGURATION: 'priceConcessionConfiguration',
};

export const DocumentExpirationDateType = {
  OTHER: 'Other',
  APPRAISAL: 'Appraisal',
  ASSETS: 'Assets',
  CREDIT: 'Credit',
  INCOME: 'Income',
  UW_DECISION: 'UWDecision',
};
